import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { Card } from '../components/UI';
import { isWFHDay } from '../utils/auth';
import { onPayroll } from '../utils/hr';

function todayStr() { return new Date().toISOString().slice(0,10); }
function fmtTime(t) { return t || '—'; }
function shiftDate(ds, n) { const d = new Date(ds+'T12:00:00'); d.setDate(d.getDate()+n); return d.toISOString().slice(0,10); }

// Determine an employee's status for a given date `d`.
// `canSeeTimes` gates exact clock-in/out times — only the admin should see them;
// employees see presence status (active / left) without the timestamps.
function getStatus(emp, hrEntry, approvedLeaves, holidays, d, isToday, canSeeTimes) {
  const t = (s) => (canSeeTimes ? s : '');   // hide time-bearing sub-text for non-admins
  const isHoliday = (holidays || []).some(h => h.date === d);
  if (isHoliday) return { label: 'Holiday', color: 'var(--purple)', sub: '' };

  const onLeave = (approvedLeaves || []).some(r =>
    r.empId === emp.id && r.status === 'approved' && d >= r.date && d <= (r.endDate || r.date)
  );
  if (onLeave) return { label: 'On Leave', color: '#C08079', sub: 'Approved' };

  const wfh = isWFHDay(emp.id, d, hrEntry?.wfhOverrides);
  const tl  = hrEntry?.timelog?.[d] || {};
  const att = hrEntry?.attendance?.[d] || '';

  // Past dates: report the recorded mark / clock log rather than live "expected" status
  if (!isToday) {
    if (tl.in && tl.out) return { label: wfh ? 'WFH — Worked' : 'Present', color: '#8FB08A', sub: t(`${fmtTime(tl.in)} – ${fmtTime(tl.out)}`) };
    if (att === 'present') return { label: 'Present', color: '#8FB08A', sub: tl.in ? t(`Since ${fmtTime(tl.in)}`) : 'Marked present' };
    if (att === 'half')    return { label: 'Half day', color: '#C79A5B', sub: '' };
    if (att === 'absent')  return { label: 'Absent', color: '#C08079', sub: 'No record' };
    if (att === 'leave')   return { label: 'On Leave', color: '#C08079', sub: '' };
    const dow = new Date(d+'T12:00:00').getDay();
    if (dow === 0 || dow === 6) return { label: 'Weekend', color: 'var(--text3)', sub: '' };
    return { label: 'No record', color: 'var(--text3)', sub: '' };
  }

  // Today: live status
  if (tl.in && tl.out) return { label: wfh ? 'WFH — Left' : 'Left Office', color: 'var(--text3)', sub: t(`${fmtTime(tl.in)} – ${fmtTime(tl.out)}`) };
  if (tl.in)           return { label: wfh ? 'WFH — Active' : 'In Office', color: '#8FB08A', sub: canSeeTimes ? `Since ${fmtTime(tl.in)}` : 'Active' };
  if (wfh)             return { label: 'WFH', color: 'var(--blue)', sub: 'Not yet clocked in' };
  return { label: 'Not Clocked In', color: '#C79A5B', sub: 'Expected in office' };
}

export default function CompanyAttendancePage({ store, canSeeTimes = true }) {
  // People with no working hours or salary (e.g. an AI agent) aren't on attendance.
  const employees = (store.employees || []).filter(e => onPayroll(store, e));
  const hr        = store.hr || {};
  const leaveReqs = store.leaveRequests || [];
  const holidays  = Array.isArray(store.holidays) ? store.holidays : [];

  const [selDate, setSelDate] = useState(todayStr());
  const isToday = selDate === todayStr();

  const summary = useMemo(() => employees.map(emp => {
    const hrEntry = hr[emp.id] || {};
    const status  = getStatus(emp, hrEntry, leaveReqs, holidays, selDate, isToday, canSeeTimes);
    const tl      = hrEntry.timelog?.[selDate] || {};
    return { emp, status, clockIn: tl.in, clockOut: tl.out };
  }), [employees, hr, leaveReqs, holidays, selDate, isToday, canSeeTimes]);

  const counts = {
    present: summary.filter(s => s.status.label === 'Present' || s.status.label === 'In Office').length,
    wfh:     summary.filter(s => s.status.label.startsWith('WFH')).length,
    notIn:   summary.filter(s => s.status.label === 'Not Clocked In' || s.status.label === 'Absent' || s.status.label === 'No record').length,
    onLeave: summary.filter(s => s.status.label === 'On Leave').length,
    left:    summary.filter(s => s.status.label.includes('Left') || s.status.label === 'WFH — Worked').length,
  };

  const statusOrder = ['In Office','Present','WFH — Active','WFH','WFH — Worked','Not Clocked In','Left Office','Half day','On Leave','Absent','Holiday','Weekend','No record'];
  const sorted = summary.slice().sort((a,b) => statusOrder.indexOf(a.status.label) - statusOrder.indexOf(b.status.label));

  const prettyDate = new Date(selDate+'T12:00:00').toLocaleDateString('en-GB',{ weekday:'long', day:'numeric', month:'long', year:'numeric' });

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      {/* Header */}
      <div>
        <h1 className="t-h1">Company Attendance</h1>
        <p className="t-sub" style={{ marginTop:4 }}>Live status today, or pick any past date to review the record.</p>
      </div>

      {/* Date navigator */}
      <Card style={{ padding:'14px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:14, flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={()=>setSelDate(shiftDate(selDate,-1))} style={navBtn}><ChevronLeft size={16}/></button>
          <div style={{ display:'flex', alignItems:'center', gap:9, minWidth:0, textAlign:'center' }}>
            <CalendarDays size={16} color="var(--accent)" style={{flexShrink:0}}/>
            <span style={{ fontFamily:'var(--serif)', fontWeight:400, fontSize:15, color:'var(--text)' }}>{prettyDate}</span>
          </div>
          <button onClick={()=>setSelDate(shiftDate(selDate,1))} disabled={isToday} style={{...navBtn, opacity:isToday?0.4:1, cursor:isToday?'not-allowed':'pointer'}}><ChevronRight size={16}/></button>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <input type="date" value={selDate} max={todayStr()} onChange={e=>e.target.value && setSelDate(e.target.value)}
            style={{ padding:'9px 12px', border:'1px solid var(--border)', borderRadius:'var(--radius)', fontFamily:'var(--font)', fontSize:13, color:'var(--text)', background:'var(--bg3)' }}/>
          {!isToday && <span onClick={()=>setSelDate(todayStr())} style={{ fontSize:13, color:'var(--accent2)', fontWeight:600, cursor:'pointer' }}>Back to today</span>}
        </div>
      </Card>

      {/* Summary strip */}
      <div className="grid-att-summary">
        {[
          [isToday?'In Office':'Present', counts.present, '#8FB08A'],
          ['WFH',       counts.wfh,     'var(--blue)'],
          [isToday?'Not In':'Absent',    counts.notIn,   '#C79A5B'],
          ['On Leave',  counts.onLeave, '#C08079'],
          [isToday?'Left':'Worked',      counts.left,    'var(--text3)'],
        ].map(([l,v,col]) => (
          <Card key={l} style={{ padding:'16px', textAlign:'center' }}>
            <div className="t-label" style={{ marginBottom:6 }}>{l}</div>
            <div style={{ fontSize:30, fontFamily:'var(--serif)', fontVariantNumeric:'lining-nums tabular-nums', fontWeight:400, color:col }}>{v}</div>
          </Card>
        ))}
      </div>

      {/* Employee list */}
      <Card>
        {sorted.map(({ emp, status, clockIn, clockOut }, i) => (
          <div key={emp.id} style={{ padding:'16px 24px', borderTop:i>0?'1px solid var(--border)':'none', display:'flex', alignItems:'center', gap:14 }}>
            <div style={{ width:40, height:40, borderRadius:'50%', background:'var(--bg3)', border:'2px solid '+status.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:700, color:'var(--text)', flexShrink:0, fontFamily:'var(--font-ui)' }}>
              {emp.name.charAt(0)}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:600, fontSize:14.5, color:'var(--text)' }}>{emp.name}</div>
              <div style={{ fontSize:12.5, color:'var(--text3)', marginTop:1 }}>{(emp.roles || [emp.role]).filter(Boolean).join(' · ')}</div>
            </div>
            <div style={{ textAlign:'right', flexShrink:0 }}>
              <div style={{ fontSize:13, fontWeight:700, color:status.color }}>{status.label}</div>
              {status.sub && <div style={{ fontSize:12, color:'var(--text3)', marginTop:1 }}>{status.sub}</div>}
            </div>
          </div>
        ))}
        {sorted.length===0 && <div style={{ padding:'2.5rem', textAlign:'center', color:'var(--text3)', fontSize:14 }}>No employees.</div>}
      </Card>
    </div>
  );
}

const navBtn = { width:36, height:36, borderRadius:'var(--radius)', border:'1px solid var(--border)', background:'var(--bg2)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text2)' };
